// src/components/Chat/AudioRecorder.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Button, ProgressBar } from 'react-bootstrap';

const AudioRecorder = ({ onRecordingComplete, onClose }) => {
  const [recording, setRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        const audioUrl = URL.createObjectURL(audioBlob);
        setRecordedAudio(audioUrl);
        setAudioBlob(audioBlob);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setRecording(true);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Unable to access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const handleSend = () => {
    if (audioBlob && onRecordingComplete) {
      // Create a File object from the Blob
      const audioFile = new File([audioBlob], `audio_recording_${Date.now()}.wav`, {
        type: 'audio/wav',
        lastModified: Date.now()
      });
      
      onRecordingComplete(audioFile);
    }
    handleClose();
  };

  const handleClose = () => {
    // Cleanup
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (recordedAudio) {
      URL.revokeObjectURL(recordedAudio);
    }
    onClose();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="audio-recorder-modal bg-white rounded shadow-lg p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">Record Audio Message</h6>
        <button 
          className="btn btn-xs btn-outline-primary ml-2"
          onClick={handleClose}
        >
          {/* <i className="bi bi-x" style={{fontSize: '1.25rem'}}></i> */}
          X
        </button>
      </div> 

      {!recordedAudio ? (
        <div className="text-center">
          <div className="mb-3">
            <i className={`fas bi bi-mic fa-3x ${recording ? 'text-danger' : 'text-muted'}`} style={{fontSize: '1.25rem'}}></i>
          </div>
          
          {recording ? (
            <>
              <div className="recording-animation mb-2">
                <div className="sound-wave">
                  <div className="bar"></div>
                  <div className="bar"></div>
                  <div className="bar"></div>
                  <div className="bar"></div>
                  <div className="bar"></div>
                </div>
              </div>
              <p className="text-danger mb-2">
                {/* <i className="bi bi-record me-2" style={{fontSize: '1.25rem'}}></i> */}
                Recording... {formatTime(recordingTime)}
              </p>
              <p className="text-muted small mb-3">
                Click stop when you're done
              </p>
              <button
                className="btn btn-danger btn-xs rounded-circle"
                onClick={stopRecording}
              >
                <i className="bi bi-sign-stop" style={{fontSize: '1.25rem'}}></i>
              </button>
            </>
          ) : (
            <>
              <p className="text-muted mb-3">
                Click the microphone to start recording
              </p>
              <button
                className="btn btn-primary btn-md rounded-circle"
                onClick={startRecording}
              >
                <i className="bi bi-mic"></i>
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="text-center">
          <div className="mb-3">
            <i className="fas fa-music fa-3x text-primary"></i>
          </div>
          
          <div className="mb-3">
            <audio controls className="w-100">
              <source src={recordedAudio} type="audio/wav" />
              Your browser does not support audio playback.
            </audio>
          </div>
          
          <p className="text-muted mb-3">
            Duration: {formatTime(recordingTime)}
          </p>
          
          <div className="d-flex justify-content-center gap-2">
            <button
              className="btn btn-outline-secondary"
              onClick={() => {
                URL.revokeObjectURL(recordedAudio);
                setRecordedAudio(null);
                setRecordingTime(0);
                setAudioBlob(null);
              }}
            >
              <i className="fas fa-redo me-2"></i>
              Re-record
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSend}
            >
              <i className="fas fa-paper-plane me-2"></i>
              Send Audio
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioRecorder;